/**
 * cameraDevices — the pure decisions behind the Capture camera picker (QA-105).
 *
 * Gordon (QA-105, recovered from QA-84 which was never dispatched): "web Capture
 * needs a CAMERA DEVICE SELECTOR (enumerateDevices) so the user can pick an
 * external/USB camera instead of only the default, plus a clear 'camera is
 * busy/in use by another app' error. Also isolate whether the original repro was
 * Zoom holding the camera exclusively."
 *
 * WHAT THE ORIGINAL REPRO ACTUALLY WAS. The browser's own error tells us: a
 * camera another application already holds exclusively rejects `getUserMedia`
 * with **`NotReadableError`** (Chromium/Firefox) or **`TrackStartError`**
 * (older Chromium) — never `NotAllowedError` and never `NotFoundError`. Before
 * this change the recorder mapped only Allowed/Found and swept everything else
 * into "Could not start the camera. Please try again", which is the single
 * least useful thing to say to someone whose camera is busy: retrying cannot
 * work until they quit the other app, and nothing on screen said so. That is
 * the whole of the reported symptom, and it is why the error branch below is
 * part of the deliverable rather than a nicety.
 *
 * No React and no browser objects here, so each decision is asserted on its own.
 */

/**
 * Where the user's last camera choice is remembered.
 *
 * `localStorage`, deliberately: a camera preference should survive closing the
 * tab, and it is a device id — not a credential, not personal data.
 */
export const LAST_CAMERA_KEY = "iyuga.capture.lastCameraDeviceId";

/** Keep only real video inputs, and only ones we can actually select by id. */
export const videoInputsOf = (devices = []) =>
  (Array.isArray(devices) ? devices : []).filter(
    (d) => d && d.kind === "videoinput" && d.deviceId
  );

/**
 * A human label for a camera.
 *
 * Browsers withhold device labels until a camera permission has been granted
 * ONCE, so an un-permissioned enumeration returns entries with `label: ""`.
 * Rendering an empty option would give the user a dropdown of blanks, so a
 * positional fallback is used instead — honest about the ordering it is really
 * offering rather than pretending to know the hardware's name.
 */
export const cameraLabel = (device, index = 0) => {
  const label = device && typeof device.label === "string" ? device.label.trim() : "";
  if (label) return label;
  return index === 0 ? "Default camera" : `Camera ${index + 1}`;
};

/**
 * Which camera should be selected?
 *
 * Precedence: the remembered choice if it is still plugged in, else the first
 * available camera, else null. A remembered id for a camera that has since been
 * unplugged must NOT be requested — `getUserMedia` would fail with
 * `OverconstrainedError` and the user would see a broken picker they never
 * touched.
 */
export const pickPreferredCamera = (devices = [], rememberedId = null) => {
  const inputs = videoInputsOf(devices);
  if (inputs.length === 0) return null;
  if (rememberedId) {
    const remembered = inputs.find((d) => d.deviceId === rememberedId);
    if (remembered) return remembered.deviceId;
  }
  return inputs[0].deviceId;
};

/**
 * The video constraint for a chosen camera.
 *
 * With a device id: `exact`, because a non-exact `deviceId` is only a HINT and
 * a browser may silently hand back a different camera — a picker that can be
 * silently ignored is worse than no picker, since the user believes they chose.
 * With no id: the previous `facingMode: "user"` behaviour, unchanged.
 */
export const cameraConstraint = (deviceId) =>
  deviceId ? { deviceId: { exact: deviceId } } : { facingMode: "user" };

/** Error names that mean "the device exists but something else is holding it". */
export const BUSY_ERROR_NAMES = ["NotReadableError", "TrackStartError", "AbortError"];

/**
 * Turn a getUserMedia rejection into a `{ status, message }` the UI can render.
 *
 * `subject` is what to name in the copy ("camera", "microphone",
 * "camera/microphone"), because the recorder's audio mode has no camera to
 * talk about.
 */
export const cameraErrorFor = (err, { subject = "camera" } = {}) => {
  const name = err && err.name;

  if (name === "NotAllowedError" || name === "SecurityError") {
    return {
      status: "denied",
      message: `${subject === "camera" ? "Camera" : "Camera/microphone"} permission was denied. Enable it for this site in your browser's address-bar settings, then try again — or upload a file instead.`,
    };
  }

  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return {
      status: "error",
      message: `No ${subject} was found. Connect one, or upload a file instead.`,
    };
  }

  // THE QA-105 BRANCH. Retrying is useless here and saying "try again" actively
  // misleads — the other application has to release the device first.
  if (BUSY_ERROR_NAMES.includes(name)) {
    return {
      status: "busy",
      message: `Your ${subject} is already in use by another app (Zoom, Teams, Meet, Photo Booth…). Quit or disconnect it there, then try again. If you have a second camera, pick it from the list.`,
    };
  }

  // A remembered camera that has since been unplugged, or a constraint this
  // device cannot satisfy. Recoverable by choosing another camera.
  if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError") {
    return {
      status: "error",
      message: `That ${subject} is no longer available. Choose another one from the list, or upload a file instead.`,
    };
  }

  return {
    status: "error",
    message: `Could not start the ${subject}. Please try again, or upload a file instead.`,
  };
};

/** Read the remembered camera id. Never throws — private mode can deny storage. */
export const readRememberedCamera = (storage) => {
  try {
    const store = storage || (typeof window !== "undefined" ? window.localStorage : null);
    return (store && store.getItem(LAST_CAMERA_KEY)) || null;
  } catch (err) {
    return null;
  }
};

/** Remember a camera id. Never throws — a failed write must not break capture. */
export const rememberCamera = (deviceId, storage) => {
  try {
    const store = storage || (typeof window !== "undefined" ? window.localStorage : null);
    if (!store || !deviceId) return false;
    store.setItem(LAST_CAMERA_KEY, deviceId);
    return true;
  } catch (err) {
    return false;
  }
};

/**
 * Whether the CURRENT runtime can capture a still photo via the camera (the
 * Image block's "Take Photo" source, WebcamPhotoCapture). Needs only
 * getUserMedia(video) — unlike canRecordMedia (Video/Audio blocks), a still
 * is drawn to a <canvas> and exported via canvas.toBlob, so no MediaRecorder /
 * codec support is required. Injectable for tests; jsdom has no
 * navigator.mediaDevices, so this returns false in every unstubbed test run.
 * @param {{mediaDevices?: object}} [deps]
 * @returns {boolean}
 */
export function canCapturePhoto({ mediaDevices } = {}) {
  const md =
    mediaDevices ||
    (typeof navigator !== "undefined" ? navigator.mediaDevices : undefined);
  return Boolean(md && typeof md.getUserMedia === "function");
}
